import { useEffect, useRef, useState } from "react";
import { Check, Loader2, Square, Volume2 } from "lucide-react";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { voicesApi, type Assistant, type ProviderVoice } from "../../api";
import { useProviderVoices, useUpdateAssistant } from "../../hooks";
import { Field } from "./controls";

function apiError(err: unknown): string {
  const e = err as { response?: { data?: { error?: string } } };
  return e?.response?.data?.error ?? "Couldn't load voices from the provider.";
}

/** Match the ElevenLabs Voice Library filters (accent covers the AU need). */
const ACCENTS = [
  ["american", "American"],
  ["australian", "Australian"],
  ["british", "British"],
  ["canadian", "Canadian"],
  ["indian", "Indian"],
  ["irish", "Irish"],
  ["new zealand", "New Zealand"],
  ["south african", "South African"],
] as const;

/** Platform-supported TTS languages (ISO 639-1, what the APIs filter on). */
const LANGUAGES = [
  ["en", "English"],
  ["es", "Spanish"],
  ["fr", "French"],
  ["de", "German"],
  ["hi", "Hindi"],
  ["ar", "Arabic"],
  ["pt", "Portuguese"],
] as const;

const ANY = "__any__";

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * Live voice picker: searches the provider's full catalog — for ElevenLabs
 * that includes the public Voice Library (thousands of voices) with
 * accent/language filters, like the ElevenLabs site. Picking a library
 * voice first adds it to the tenant's provider account (required for TTS);
 * only tts_voice_id/name are stored on the assistant.
 */
export default function VoicePicker({ assistant }: { assistant: Assistant }) {
  const provider = assistant.voice?.provider ?? "";
  const updateMut = useUpdateAssistant(assistant.id);
  const [search, setSearch] = useState("");
  const [accent, setAccent] = useState("");
  const [language, setLanguage] = useState("");
  const debouncedSearch = useDebounced(search.trim(), 400);
  const q = useProviderVoices(provider, {
    search: debouncedSearch || undefined,
    accent: accent || undefined,
    language: language || undefined,
  });
  const [claiming, setClaiming] = useState("");
  const [playing, setPlaying] = useState("");
  const [showPaste, setShowPaste] = useState(false);
  // Local draft for the paste field — saved on blur/Enter, not per keystroke.
  const [customId, setCustomId] = useState(assistant.tts_voice_id);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    setCustomId(assistant.tts_voice_id);
  }, [assistant.tts_voice_id]);

  useEffect(() => {
    return () => audioRef.current?.pause();
  }, []);

  const voices = q.data ?? [];
  const showFilters = provider === "elevenlabs" || provider === "cartesia";

  async function choose(v: ProviderVoice) {
    if (!v.public_owner_id) {
      updateMut.mutate({ tts_voice_id: v.voice_id, tts_voice_name: v.name });
      return;
    }
    // Library voice: add it to the tenant's provider account first.
    setClaiming(v.voice_id);
    try {
      const voiceId = await voicesApi.add({
        provider,
        voice_id: v.voice_id,
        public_owner_id: v.public_owner_id,
        name: v.name,
      });
      updateMut.mutate({ tts_voice_id: voiceId, tts_voice_name: v.name });
      toast.success(`"${v.name}" added to your account and selected.`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setClaiming("");
    }
  }

  function togglePreview(v: ProviderVoice) {
    audioRef.current?.pause();
    if (playing === v.voice_id) {
      setPlaying("");
      return;
    }
    const audio = new Audio(v.preview_url);
    audio.onended = () => setPlaying("");
    audioRef.current = audio;
    setPlaying(v.voice_id);
    audio.play().catch(() => setPlaying(""));
  }

  if (!provider) {
    return (
      <p className="rounded-lg border border-dashed p-3 text-xs text-muted-foreground">
        Pick a provider and TTS model above first — then choose the exact
        voice here.
      </p>
    );
  }

  return (
    <Field
      label="Speaking voice"
      hint="Loaded live from the provider — your voices plus their full voice library."
    >
      <div className="space-y-2">
        {showFilters && (
          <div className="flex flex-wrap gap-2">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search voices…"
              className="h-9 min-w-36 flex-1"
            />
            <Select
              value={accent || ANY}
              onValueChange={(v) => setAccent(v === ANY ? "" : v)}
            >
              <SelectTrigger className="h-9 w-32">
                <SelectValue placeholder="Accent" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Any accent</SelectItem>
                {ACCENTS.map(([v, label]) => (
                  <SelectItem key={v} value={v}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={language || ANY}
              onValueChange={(v) => setLanguage(v === ANY ? "" : v)}
            >
              <SelectTrigger className="h-9 w-32">
                <SelectValue placeholder="Language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Any language</SelectItem>
                {LANGUAGES.map(([v, label]) => (
                  <SelectItem key={v} value={v}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {q.isLoading && (
          <div className="flex items-center gap-2 rounded-lg border border-dashed p-3 text-xs text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" />
            Fetching voices…
          </div>
        )}

        {q.isError && (
          <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs text-amber-600 dark:text-amber-500">
            {apiError(q.error)}
          </p>
        )}

        {!q.isLoading && !q.isError && (
          <div className="relative max-h-64 overflow-y-auto rounded-lg border">
            {q.isFetching && (
              <div className="sticky top-0 flex items-center gap-2 border-b bg-background/95 px-2 py-1 text-xs text-muted-foreground">
                <Loader2 className="size-3 animate-spin" />
                Updating…
              </div>
            )}
            {voices.length === 0 && (
              <p className="p-3 text-xs text-muted-foreground">
                No voices found for these filters.
              </p>
            )}
            {voices.map((v) => {
              const meta = [v.accent, v.gender, v.language]
                .filter(Boolean)
                .join(" · ");
              const isSelected = v.voice_id === assistant.tts_voice_id;
              return (
                <div
                  key={v.voice_id}
                  className={cn(
                    "flex items-center gap-2 border-b px-2 py-1.5 last:border-b-0",
                    isSelected && "bg-muted",
                  )}
                >
                  <button
                    type="button"
                    disabled={!!claiming || updateMut.isPending}
                    onClick={() => choose(v)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <span className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-medium">{v.name}</span>
                      {v.source === "library" && (
                        <span className="shrink-0 rounded bg-muted px-1 py-px text-[10px] uppercase tracking-wide text-muted-foreground">
                          Library
                        </span>
                      )}
                    </span>
                    {meta && (
                      <span className="block truncate text-xs text-muted-foreground">
                        {meta}
                      </span>
                    )}
                  </button>
                  {claiming === v.voice_id && (
                    <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
                  )}
                  {v.preview_url && (
                    <button
                      type="button"
                      onClick={() => togglePreview(v)}
                      title={playing === v.voice_id ? "Stop preview" : "Play preview"}
                      className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      {playing === v.voice_id ? (
                        <Square className="size-3.5" />
                      ) : (
                        <Volume2 className="size-3.5" />
                      )}
                    </button>
                  )}
                  {isSelected && <Check className="size-4 shrink-0 text-primary" />}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          {assistant.tts_voice_id ? (
            <p className="text-xs text-muted-foreground">
              Selected:{" "}
              <span className="font-medium text-foreground">
                {assistant.tts_voice_name || assistant.tts_voice_id}
              </span>
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              No voice selected — the default voice is used.
            </p>
          )}
          <button
            type="button"
            onClick={() => setShowPaste((s) => !s)}
            className="shrink-0 text-xs text-muted-foreground underline-offset-2 hover:underline"
          >
            {showPaste ? "Hide voice ID" : "Paste voice ID"}
          </button>
        </div>
        {showPaste && (
          <Input
            value={customId}
            onChange={(e) => setCustomId(e.target.value)}
            onBlur={() => {
              const id = customId.trim();
              if (id === assistant.tts_voice_id) return;
              const known = voices.find((v) => v.voice_id === id);
              updateMut.mutate({ tts_voice_id: id, tts_voice_name: known?.name ?? "" });
            }}
            onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
            placeholder="Voice ID from your provider account"
            className="h-9 font-mono text-xs"
          />
        )}
      </div>
    </Field>
  );
}
